export interface SpecialPriceData {
  currentPrice?: number | string | false | null
  nextPrice?: number | string | false | null
  dateNextPrice?: Date | string | false | null
  currentPriceValidUntil?: Date | string | false | null
}

export type SpecialPriceSource = 'base' | 'special'

export interface ResolvedSpecialPriceState {
  currentPrice: number | false | undefined
  currentSource: SpecialPriceSource
  nextPrice: number | false | null
  nextDate: Date | false | null
  nextSource: SpecialPriceSource | null
  hasNextPrice: boolean
  hasActiveSpecialPrice: boolean
  hasFutureSpecialPrice: boolean
  hasEndingSpecialPrice: boolean
  hasEnabledSpecialPrice: boolean
}

export type ConsumerPriceData = SpecialPriceData
export type ConsumerPriceSource = SpecialPriceSource
export type ResolvedConsumerPriceState = ResolvedSpecialPriceState

function hasPrice(value: SpecialPriceData['currentPrice']): boolean {
  if (value === false || value === null || value === undefined) {
    return false
  }
  return !Number.isNaN(parseFloat(String(value)))
}

function normalizePrice(value: SpecialPriceData['currentPrice']): number | false | undefined {
  if (value === undefined) {
    return undefined
  }
  if (!hasPrice(value)) {
    return false
  }
  return parseFloat(String(value))
}

function normalizeDate(value: SpecialPriceData['dateNextPrice']): Date | false | null {
  if (!value) {
    return false
  }
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? false : date
}

function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate())
}

function isSameOrAfterDay(value: Date, reference: Date): boolean {
  return startOfDay(value).getTime() >= startOfDay(reference).getTime()
}

function isSameOrBeforeDay(value: Date, reference: Date): boolean {
  return startOfDay(value).getTime() <= startOfDay(reference).getTime()
}

function resolveBaseNextPrice(basePrices: SpecialPriceData): Pick<ResolvedSpecialPriceState, 'nextPrice' | 'nextDate' | 'nextSource' | 'hasNextPrice'> {
  if (!hasPrice(basePrices.nextPrice)) {
    return {
      nextPrice: null,
      nextDate: null,
      nextSource: null,
      hasNextPrice: false,
    }
  }

  return {
    nextPrice: normalizePrice(basePrices.nextPrice) ?? false,
    nextDate: normalizeDate(basePrices.dateNextPrice),
    nextSource: 'base',
    hasNextPrice: true,
  }
}

function resolveBasePriceAfter(basePrices: SpecialPriceData, date: Date): number | false | undefined {
  const baseNextDate = normalizeDate(basePrices.dateNextPrice)
  if (hasPrice(basePrices.nextPrice) && baseNextDate && isSameOrBeforeDay(baseNextDate, date)) {
    return normalizePrice(basePrices.nextPrice)
  }
  return normalizePrice(basePrices.currentPrice)
}

export function resolveSpecialPriceState(
  basePrices: SpecialPriceData,
  specialPrices?: SpecialPriceData | null,
  today = new Date()
): ResolvedSpecialPriceState {
  const baseCurrentPrice = normalizePrice(basePrices.currentPrice)
  const specialCurrentPrice = normalizePrice(specialPrices?.currentPrice)
  const specialEndDate = normalizeDate(specialPrices?.currentPriceValidUntil)
  const specialNextPrice = normalizePrice(specialPrices?.nextPrice)
  const specialNextDate = normalizeDate(specialPrices?.dateNextPrice)

  const hasActiveSpecialPrice = hasPrice(specialCurrentPrice) && (!specialEndDate || isSameOrAfterDay(specialEndDate, today))
  const hasFutureSpecialPrice = hasPrice(specialNextPrice) && !!specialNextDate && isSameOrAfterDay(specialNextDate, today)
  const hasEndingSpecialPrice = hasActiveSpecialPrice && !!specialEndDate && !hasFutureSpecialPrice
  const hasAnyFuturePrice = baseCurrentPrice != undefined || specialNextPrice != undefined

  if (hasActiveSpecialPrice) {
    const nextFuturePriceIsDifferent = specialCurrentPrice != specialNextPrice

    if (hasFutureSpecialPrice) {
      return {
        currentPrice: specialCurrentPrice,
        currentSource: 'special',
        nextPrice: specialNextPrice ?? false,
        nextDate: specialNextDate,
        nextSource: 'special',
        hasNextPrice: nextFuturePriceIsDifferent,
        hasActiveSpecialPrice,
        hasFutureSpecialPrice,
        hasEndingSpecialPrice,
        hasEnabledSpecialPrice: true,
      }
    }

    if (specialEndDate) {
      return {
        currentPrice: specialCurrentPrice,
        currentSource: 'special',
        nextPrice: resolveBasePriceAfter(basePrices, specialEndDate) ?? false,
        nextDate: specialEndDate,
        nextSource: 'base',
        hasNextPrice: hasAnyFuturePrice,
        hasActiveSpecialPrice,
        hasFutureSpecialPrice,
        hasEndingSpecialPrice,
        hasEnabledSpecialPrice: false,
      }
    }

    return {
      currentPrice: specialCurrentPrice,
      currentSource: 'special',
      nextPrice: null,
      nextDate: null,
      nextSource: null,
      hasNextPrice: false,
      hasActiveSpecialPrice,
      hasFutureSpecialPrice,
      hasEndingSpecialPrice,
      hasEnabledSpecialPrice: true,
    }
  }

  if (hasFutureSpecialPrice) {
    return {
      currentPrice: baseCurrentPrice,
      currentSource: 'base',
      nextPrice: specialNextPrice ?? false,
      nextDate: specialNextDate,
      nextSource: 'special',
      hasNextPrice: true,
      hasActiveSpecialPrice,
      hasFutureSpecialPrice,
      hasEndingSpecialPrice,
      hasEnabledSpecialPrice: true,
    }
  }

  return {
    currentPrice: baseCurrentPrice,
    currentSource: 'base',
    ...resolveBaseNextPrice(basePrices),
    hasActiveSpecialPrice,
    hasFutureSpecialPrice,
    hasEndingSpecialPrice,
    hasEnabledSpecialPrice: false,
  }
}

export const resolveConsumerPriceState = resolveSpecialPriceState
